import React, { useState, useRef, useCallback } from 'react';
import { getCardName, getCardTypeClass, getUILoc } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';

const SWIPE_THRESHOLD = 30;
const LONG_PRESS_DURATION = 200;

const DeckBuilderLibraryCard = ({ id, card, onTap, onSwipeLeft, onMouseEnter, onMouseMove, onMouseLeave }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const pressTimeout = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isSwipe = useRef(false);
  const lastTapTime = useRef(0);

  const cardName = getCardName(card.id);
  const cardTypeClass = getCardTypeClass(card.card_type);

  const handlePressStart = useCallback((e) => {
    startX.current = e.clientX || (e.touches && e.touches[0].clientX);
    currentX.current = startX.current;
    isSwipe.current = false;
    setIsPressed(true);

    pressTimeout.current = setTimeout(() => {
      const movedX = Math.abs(currentX.current - startX.current);
      if (movedX < 10) {
        setShowHint(true);
      }
    }, LONG_PRESS_DURATION);
  }, []);

  const handlePressMove = useCallback((e) => {
    if (onMouseMove) onMouseMove(e);
    if (!isPressed) return;

    currentX.current = e.clientX || (e.touches && e.touches[0].clientX);
    const movedX = startX.current - currentX.current;

    if (movedX > 10) {
        isSwipe.current = true;
        clearTimeout(pressTimeout.current);
        setShowHint(false);
    }
  }, [isPressed, onMouseMove]);

  const handlePressEnd = useCallback(() => {
    clearTimeout(pressTimeout.current);
    const movedX = startX.current - currentX.current;

    if (isPressed) {
      if (movedX > SWIPE_THRESHOLD) {
        if (onSwipeLeft) onSwipeLeft(card);
      } else if (Math.abs(movedX) < 10) {
        const now = Date.now();
        if (onTap && (now - lastTapTime.current > 100)) {
          lastTapTime.current = now;
          onTap(card);
        }
      }
    }
    setIsPressed(false);
    setShowHint(false);
    startX.current = 0;
    currentX.current = 0;
    isSwipe.current = false;
  }, [isPressed, card, onTap, onSwipeLeft]);
  
  const handleMouseLeave = useCallback((e) => {
    if (onMouseLeave) onMouseLeave(e);
    if (isPressed) {
        setIsPressed(false);
        setShowHint(false);
        clearTimeout(pressTimeout.current);
    }
  }, [isPressed, onMouseLeave]);

  return (
    <div
      id={id}
      className={`deck-builder-library-card card-type-${cardTypeClass} ${isPressed ? 'pressed' : ''}`}
      onMouseDown={(e) => { handlePressStart(e); if (onMouseEnter) onMouseEnter(e); }}
      onMouseMove={handlePressMove}
      onMouseUp={handlePressEnd}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={onMouseEnter}
      onTouchStart={handlePressStart}
      onTouchMove={handlePressMove}
      onTouchEnd={handlePressEnd}
      title={getUILoc('LABELS.CLICK_TO_ADD_FLICK_FOR_DETAIL')}
    >
      <CardIllustration cardId={card.id} isBackground={true} />
      {showHint && <div className="card-swipe-hint">{getUILoc('LABELS.FLICK_FOR_DETAIL')}</div>}
      <span className="card-name-label">{cardName} ({card.required_scale})</span>
    </div>
  );
};

export default DeckBuilderLibraryCard;
